import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Event/SystemEventSettings/SystemEventSettings.less";
import { Card, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { produce } from "immer";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { isEqual } from "lodash";
import { selectSystemEvent, setSystemEventAction } from "Data/Redux/Slices/Settings/Event/System";
import { eventPopoverContent } from "Data/Objects/Event";

type SystemEventForm = {
	alarmOut: HTMLInputElement;
	snapshot: HTMLInputElement;
	email: HTMLInputElement;
	ftp: HTMLInputElement;
} & HTMLFormElement;

function SystemEventSettings() {
	const dispatch = useAppDispatch();
	const systemEventSettings = useAppSelector(selectSystemEvent);
	const [ localSystemEventSettings, setLocalSystemEventSettings ] = useState(systemEventSettings);
	const setSystemEvent = () => dispatch(setSystemEventAction(localSystemEventSettings));
	const disabled = isEqual(localSystemEventSettings, systemEventSettings);
	const { alarmOut, snapshot, email, ftp } = localSystemEventSettings;

	const handleAlarmOutChange = (newEnabled: boolean) => {
		setLocalSystemEventSettings(produce(localSystemEventSettings, draft => {
			draft.alarmOut = newEnabled;
		}));
	};

	const handleSnapshotChange = (newEnabled: boolean) => {
		setLocalSystemEventSettings(produce(localSystemEventSettings, draft => {
			draft.snapshot = newEnabled;
		}));
	};

	const handleEmailChange = (newEnabled: boolean) => {
		setLocalSystemEventSettings(produce(localSystemEventSettings, draft => {
			draft.email = newEnabled;
		}));
	};

	const handleFtpChange = (newEnabled: boolean) => {
		setLocalSystemEventSettings(produce(localSystemEventSettings, draft => {
			draft.ftp = newEnabled;
		}));
	};

	const handleFormSubmit = (event: ReactSubmitEvent<SystemEventForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setSystemEvent();
		}
	};

	const handleFormReset = (event: FormEvent<SystemEventForm>) => {
		event.preventDefault();

		setLocalSystemEventSettings(systemEventSettings);
	};

	return (
		<SettingsLayout.Form
			className="system-event-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="System Event Settings" popoverContent={ eventPopoverContent }>
					<div className="system-event-content">
						<div className="form-row">
							<span>Alarm Out</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAlarmOutChange } checked={ alarmOut } />
							</div>
						</div>
						<div className="form-row">
							<span>Snapshot</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleSnapshotChange } checked={ snapshot } />
							</div>
						</div>
						<div className="form-row">
							<span>Email</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleEmailChange } checked={ email } />
							</div>
						</div>
						<div className="form-row">
							<span>FTP</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleFtpChange } checked={ ftp } />
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(SystemEventSettings);
